import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { login } from '@/api/auth'
import { useAuthToken } from '@/hooks/useAuthToken'

export function AccessScreen() {
  const { token, setSession } = useAuthToken()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (session) => {
      setSession(session)
      navigate('/')
    },
  })

  if (token) return <Navigate to="/" replace />

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!email.trim() || !password.trim()) return
    loginMutation.mutate({ email, password })
  }

  // The backend's only documented failure mode for this endpoint is a 401
  // (GUI_AUTH_002_LOGIN_FAILED, identical body regardless of which field was
  // wrong) - shown generically here regardless of the parsed code, so a
  // malformed/unparsed error body still surfaces something to the operator.
  const loginFailed = loginMutation.isError

  return (
    <div className="flex h-screen items-center justify-center bg-black text-text-primary">
      <form
        onSubmit={handleSubmit}
        className="flex w-[320px] flex-col gap-4 rounded-control border border-border bg-surface p-6"
      >
        <div>
          <div className="text-title font-semibold">Legal Flow</div>
          <div className="text-body text-text-secondary">Configuration Manager</div>
        </div>

        <label className="flex flex-col gap-1 text-body">
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary"
          />
        </label>

        <label className="flex flex-col gap-1 text-body">
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary"
          />
        </label>

        {loginFailed && (
          <div className="flex items-center gap-2 text-body text-danger">
            <AlertCircle size={16} strokeWidth={1.5} />
            Incorrect email or password
          </div>
        )}

        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:opacity-50"
        >
          {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}
